import * as React from "react";
import { Autocomplete, Box, Grid, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import FlexGrid from "../../../../layouts/flexGrid";
import {
    useGetDistinctActionsQuery,
    useSearchSessionLogMutation,
} from "../../../../services/cclTokenedCustomerHierarchyApi";
import { SessionLogRequest } from "../../../../services/types/clientTypes";
import CclErrorDialog from "../../../../components/common/cclErrorDialog";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import CclSearchDefault from "../../../../components/common/cclSearchDefault";
import ComponentLoader from "../../../../components/common/componentLoader";
import SessionLogDataGridPro from "./sessionLogDataGridPro";

interface SessionLogPanelProps {}

const SessionLogPanel: React.FC<SessionLogPanelProps> = () => {
    const [searchSessionId, setSearchSessionId] = React.useState<string>("");
    const [searchAction, setSearchAction] = React.useState<string>("");
    const [searchDetail, setSearchDetail] = React.useState<string>("");
    const [searchFromSessionTime, setSearchFromSessionTime] = React.useState<Date | null>(null);
    const [searchToSessionTime, setSearchToSessionTime] = React.useState<Date | null>(null);
    const [showSearchToBroad, setShowSearchToBroad] = React.useState<boolean>(false);

    const [searchSessionLog, { data: sessionLogData, isLoading, isSuccess, isUninitialized }] =
        useSearchSessionLogMutation();
    const { data: actionOptions } = useGetDistinctActionsQuery();

    React.useEffect(() => {
        if (isSuccess && sessionLogData && sessionLogData.length >= 30000)
            setShowSearchToBroad(true);
    }, [sessionLogData, isSuccess]);

    const getSessionLogs = async () => {
        const payload: SessionLogRequest = {
            sessionId: searchSessionId ? parseInt(searchSessionId) : undefined,
            action: searchAction,
            detail: searchDetail,
            fromSessionTime: searchFromSessionTime,
            toSessionTime: searchToSessionTime,
            wildcardSearch: true,
        };
        searchSessionLog(payload);
    };

    const handleSearchKeyPress = async (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === "Enter") {
            evt.preventDefault();
            getSessionLogs();
        }
    };

    return (
        <Stack height={1} width={1}>
            <CclErrorDialog
                open={showSearchToBroad}
                title={"Too Many Results"}
                msg={
                    "There are too many results for this search. Consider adjusting your search parameters and searching again."
                }
                onOk={() => setShowSearchToBroad(false)}
            />
            <Grid item xs={12} md={10}>
                <Box
                    sx={{
                        m: 2,
                        width: 1,
                    }}
                >
                    <Grid container component="form" sx={{ alignItems: "center" }}>
                        <Grid item xs={6} md={2} sx={{ p: 1 }}>
                            <TextField
                                value={searchSessionId}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="session-log-search-session-id"
                                label="Enter User Name"
                                variant="outlined"
                                onChange={(e) => {
                                    e.preventDefault();
                                    setSearchSessionId(e.target.value.replace(/[^0-9]/g, ""));
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={2} sx={{ p: 1 }}>
                            <Autocomplete
                                id="session-log-search-action"
                                options={actionOptions ?? []}
                                value={searchAction || null}
                                onChange={(e, newValue) => setSearchAction(newValue ?? "")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        sx={{ width: 1, maxHeight: "57px" }}
                                        label="Change Type"
                                        variant="outlined"
                                        onKeyPress={(e) => handleSearchKeyPress(e)}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} md={2} sx={{ p: 1 }}>
                            <TextField
                                value={searchDetail}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="session-log-search-detail"
                                label="Enter Detail"
                                variant="outlined"
                                onChange={(e) => {
                                    e.preventDefault();
                                    setSearchDetail(e.target.value);
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={2} sx={{ p: 1 }}>
                            <DatePicker
                                label="From Date"
                                value={searchFromSessionTime}
                                onChange={(newValue) => setSearchFromSessionTime(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} sx={{ width: 1, maxHeight: "57px" }} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} md={2} sx={{ p: 1 }}>
                            <DatePicker
                                label="To Date"
                                value={searchToSessionTime}
                                onChange={(newValue) => setSearchToSessionTime(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} sx={{ width: 1, maxHeight: "57px" }} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} md={1} sx={{ p: 1 }}>
                            <CclButton
                                onClick={() => getSessionLogs()}
                                aria-label="Search session log"
                                sx={{ width: 1, height: 1, maxHeight: "57px", marginLeft: "10px" }}
                                mode={"primary"}
                            >
                                Search
                            </CclButton>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <Box
                sx={{
                    width: 1,
                    height: 1,
                    m: 2,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {isLoading ? (
                    <ComponentLoader msg="Loading" />
                ) : isUninitialized ? (
                    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <CclSearchDefault
                            line1="Start searching for Session Log"
                            line2="Enter search criteria and click Search"
                            hasButtons={false}
                        />
                    </Box>
                ) : (
                    <FlexGrid>
                        <SessionLogDataGridPro
                            sessionLogList={sessionLogData ?? []}
                            isLoading={isLoading}
                            showToolbar={!isLoading}
                        />
                    </FlexGrid>
                )}
            </Box>
        </Stack>
    );
};

export default SessionLogPanel;
